import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { VALID_CATEGORY_VALUES } from 'src/Components/PracticalInformation/practicalInformationTypeColor.helper';

const SEARCH_PARAM_KEY = 'categories';

interface UsePracticalInformationFiltersResult {
  selectedCategories: string[];
  setSelectedCategories: (next: string[]) => void;
}

// Synchronise les catégories sélectionnées avec le query param `?categories=…`.
// On filtre les valeurs hors enum pour ne pas crasher sur un deep-link malformé.
const usePracticalInformationFilters = (): UsePracticalInformationFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedCategories = useMemo<string[]>(() => {
    const raw = searchParams.get(SEARCH_PARAM_KEY);
    if (!raw) {
      return [];
    }
    return raw
      .split(',')
      .map(value => value.trim())
      .filter(value => VALID_CATEGORY_VALUES.has(value));
  }, [searchParams]);

  const setSelectedCategories = useCallback(
    (next: string[]) => {
      const cleaned = next.filter(value => VALID_CATEGORY_VALUES.has(value));
      const newParams = new URLSearchParams(searchParams);
      if (cleaned.length === 0) {
        newParams.delete(SEARCH_PARAM_KEY);
      } else {
        newParams.set(SEARCH_PARAM_KEY, cleaned.join(','));
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return { selectedCategories, setSelectedCategories };
};

export default usePracticalInformationFilters;
